import type {
  OfflineStatusChangeAction,
  OfflineScheduleRetryAction,
  OfflineAction,
  OfflineState,
  ResultAction,
  Config,
  Enqueue,
  Dequeue
} from './types';
import {
  OFFLINE_STATUS_CHANGED,
  OFFLINE_SCHEDULE_RETRY,
  OFFLINE_COMPLETE_RETRY,
  OFFLINE_BUSY,
  RESET_STATE
} from './constants';
import {LOAD} from '../cache/constants'

export const initialState: OfflineState = {
  busy: false,
  lastTransaction: 0,
  online: false,
  outbox: [],
  retryCount: 0,
  retryScheduled: false
};

export const buildOfflineUpdater = (dequeue: Dequeue, enqueue: Enqueue) =>
  function offlineUpdater(
    state: OfflineState = initialState,
    action:
      | OfflineStatusChangeAction
      | OfflineScheduleRetryAction
      | ResultAction
      | OfflineStatusChangeAction
  ): OfflineState {

    // Update online/offline status
    if (action.type === OFFLINE_STATUS_CHANGED && !action.meta) {      
      return {
        ...state,
        online: action.payload.online,
      };
    }

    if (action.type === OFFLINE_SCHEDULE_RETRY) {
      return {
        ...state,
        retryScheduled: true,
        retryCount: state.retryCount + 1
      };
    }

    if (action.type === OFFLINE_COMPLETE_RETRY) {
      return { ...state, retryScheduled: false };
    }

    if (
      action.type === OFFLINE_BUSY &&      
      action.payload &&
      typeof action.payload.busy === 'boolean'
    ) {
      return { ...state, busy: action.payload.busy };
    }

    // Add offline actions to queue
    if (action.meta && action.meta.offline) {
      const transaction = state.lastTransaction + 1;
      const stamped = ({
        ...action,
        meta: { ...action.meta, transaction }
      } as any) as OfflineAction;
      const offline = state;
      return {
        ...state,
        lastTransaction: transaction,
        outbox: enqueue(offline.outbox, stamped, { offline })
      };
    }

    // Remove completed actions from queue (success or fail)
    if (action.meta && action.meta.completed === true) {
      const offline = state;
      return {
        ...state,
        outbox: dequeue(offline.outbox, action, { offline }),
        retryCount: 0
      };
    }

    if (action.type === RESET_STATE) {
      return {
        ...initialState,
        online: state.online,
      };
    }

    if (action.type === LOAD) {
      return {
        ...state,
        online: navigator.onLine, // Adding this line to persists the online status check
        busy: false,
        retryScheduled: false
      };
    }    

    return state;
  };

export const enhanceReducer = (reducer: any, config: Config) => {
  const { dequeue, enqueue } = config.queue!;
  const offlineUpdater = buildOfflineUpdater(dequeue, enqueue);

  return (state: any, action: any): any => {
    let offlineState;
    let restState;
    if (typeof state !== 'undefined') {
      offlineState = config.offlineStateLens(state).get;
      restState = config.offlineStateLens(state).set();
    }

    return config
      .offlineStateLens(reducer(restState, action))
      .set(offlineUpdater(offlineState, action));
  };
};
